import { AdminKeysSecretsLastRotation } from "../../models/admin-keys-secrets.models";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { twMerge } from "tailwind-merge";

type Props = {
  mailboxConfigured: boolean;
  lastRotation?: AdminKeysSecretsLastRotation;
  hasPendingRotation: boolean;
  isRotationOverdue: boolean;
  rotationDueDate?: string;
  canMarkRotationDone: boolean;
  isSaving: boolean;
  onRotationDone: () => void;
};

function formatDateOnly(value?: string) {
  if (!value) {
    return "not recorded";
  }

  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return value;
  }

  return new Date(timestamp).toISOString().slice(0, 10);
}

const AdminKeysSecretsRotationBar = ({
  mailboxConfigured,
  lastRotation,
  hasPendingRotation,
  isRotationOverdue,
  rotationDueDate,
  canMarkRotationDone,
  isSaving,
  onRotationDone,
}: Props) => {
  const rotationLabel = lastRotation
    ? `Last rotation: ${formatDateOnly(lastRotation.rotatedAt)} by ${lastRotation.rotatedBy}`
    : "Last rotation: not recorded";
  const dueLabel = rotationDueDate ? `Rotation due: ${formatDateOnly(rotationDueDate)}` : "Rotation due: unavailable";
  let batchStatusLabel = "No unacknowledged complete key batch.";
  if (!mailboxConfigured) {
    batchStatusLabel = "Mailbox not configured.";
  } else if (hasPendingRotation) {
    batchStatusLabel = dueLabel;
  }

  return (
    <section
      data-test="admin-keys-secrets-rotation-bar"
      className="flex flex-wrap items-center justify-between gap-3 border border-[#D7E2DE] bg-white px-4 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold" data-test="admin-keys-secrets-last-rotation">
          {rotationLabel}
        </p>
        <p
          className={twMerge(
            "mt-1 text-xs font-semibold",
            hasPendingRotation ? "text-[#BF0002]" : "text-[#4A5655] dark:text-gray-500",
            isRotationOverdue && "text-[#A00002]",
          )}
          data-test="admin-keys-secrets-rotation-due-date"
        >
          {batchStatusLabel}
        </p>
      </div>
      <button
        type="button"
        data-test="admin-keys-secrets-rotation-done"
        disabled={!canMarkRotationDone || isSaving}
        onClick={onRotationDone}
        className="inline-flex h-10 flex-none items-center justify-center gap-2 bg-green px-4 text-sm font-semibold text-white transition-colors hover:bg-[#166b60] disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
      >
        <CheckCircleIcon className="h-4 w-4" />
        {isSaving ? "Saving..." : "Rotation done"}
      </button>
    </section>
  );
};

export default AdminKeysSecretsRotationBar;
